import MuiButton, {
  ButtonProps as MuiButtonProps,
  ButtonPropsColorOverrides,
  ButtonPropsSizeOverrides,
  ButtonPropsVariantOverrides,
} from '@mui/material/Button';
import { OverridableStringUnion } from '@mui/types';
import { ComponentPropsWithoutRef, ComponentPropsWithRef, ElementType, forwardRef, ReactElement } from 'react';
import { styled } from '@mui/material/styles';
import { CircularProgress as MuiCircularProgress, CircularProgressProps } from '@mui/material';
import { DisplayNameObj } from '../theme/types';

// CircularProgress specification - sizing specific for button: from Design library -- font size of button variants.
const CPSIZE = {
  SMALL: 12,
  MEDIUM: 14,
  LARGE: 18,
};

interface CPAddedMarginProps {
  mx?: string;
  my?: string;
}

interface StyledCPOwnerState {
  ownerState: CPAddedMarginProps;
}

const StyledCircularProgress = styled(MuiCircularProgress)<StyledCPOwnerState>(({ ownerState }) => ({
  ...(ownerState.mx && {
    marginLeft: ownerState.mx,
    marginRight: ownerState.mx,
  }),
  ...(ownerState.my && {
    marginTop: ownerState.my,
    marginBottom: ownerState.my,
  }),
}));

declare module '@mui/material/Button' {
  export interface ButtonPropsColorOverrides {
    internal: true;
  }

  export interface ButtonPropsSizeOverrides {
    uniform: true;
  }

  export interface ButtonPropsVariantOverrides {
    opaque: true;
    dashed: true;
  }
}

export * from '@mui/material/Button';

export type ButtonProps<T extends ElementType = 'button'> = Omit<MuiButtonProps<T>, 'component'> & {
  color?: OverridableStringUnion<
    'primary' | 'secondary' | 'internal' | 'success' | 'error' | 'inherit',
    ButtonPropsColorOverrides
  >;
  /**
   * Same as Material UI variant, except 'text' variant is semi-transparent.
   */
  variant?: OverridableStringUnion<
    'text' | 'contained' | 'outlined' | 'opaque' | 'dashed',
    ButtonPropsVariantOverrides
  >;
  size?: OverridableStringUnion<'small' | 'medium' | 'large' | 'uniform', ButtonPropsSizeOverrides>;

  /**
   * What component to render this component as (inheriting props).
   * Useful when combining with e.g. react-router.
   */
  component?: T;

  ref?: ComponentPropsWithRef<T>['ref'];

  loading?: boolean;
  loadingText?: string | ReactElement;
} & ComponentPropsWithoutRef<T>;

/**
 * Default button has outlined variant. We also have extra colors.
 */
const Button = forwardRef(<T extends ElementType = 'button'>(props: ButtonProps<T>, ref: ButtonProps['ref']) => {
  const {
    color = 'primary',
    variant = 'outlined',
    size = 'medium',
    startIcon = null,
    loading,
    loadingText,
    ...other
  } = props;

  if (loading) {
    const cpSize: CircularProgressProps['size'] =
      size === 'small' ? CPSIZE.SMALL : size === 'large' ? CPSIZE.LARGE : CPSIZE.MEDIUM;

    const cpOwnerState: CPAddedMarginProps = {
      mx: size === 'small' || size === 'medium' ? '3px' : size === 'large' ? '2px' : undefined,
      my: size === 'uniform' ? '5px' : undefined,
    };

    return (
      <MuiButton
        ref={ref}
        color={color}
        variant={variant}
        size={size}
        {...other}
        disabled
        startIcon={<StyledCircularProgress color="inherit" size={cpSize} ownerState={cpOwnerState} />}
        endIcon={undefined}
      >
        {loadingText ?? props.children}
      </MuiButton>
    );
  }

  return <MuiButton ref={ref} color={color} variant={variant} size={size} startIcon={startIcon} {...other} />;
}) as (<T extends ElementType = 'button'>(props: ButtonProps<T>) => ReactElement | null) & DisplayNameObj;

Button.displayName = 'Button';

export default Button;
